import React from "react";
import { siteClient } from "../helpers/siteApi";

/* ================== TYPES ================== */
export type MetaField =
    | {
    name: string;
    label: string;
    type: "string" | "text" | "html";
    required?: boolean;
    default?: any;
    help?: string;
    placeholder?: string;
}
    | {
    name: string;
    label: string;
    type: "checkbox";
    default?: boolean;
    help?: string;
}
    | {
    name: string;
    label: string;
    type: "select";
    options: Array<{ value: string; label: string }>;
    default?: string;
    help?: string;
}
    | {
    name: string;
    label: string;
    type: "repeater";
    itemLabel?: string;
    fields: MetaField[];
    default?: any[];
    help?: string;
}
    | {
    name: string;
    label: string;
    type: "image";
    default?: string;
    help?: string;
    placeholder?: string;
};

export type SectionMeta = {
    title?: string;
    notes?: string | null;
    fields?: MetaField[];
};

type Props = {
    meta: SectionMeta;
    value: any;
    onChange: (next: any) => void;
};

type ImageValue = string | { url?: string; path?: string; extension?: string };
function isObj(v: any): v is { url?: string; path?: string } {
    return v && typeof v === "object";
}

function resolvePreviewUrl(v: ImageValue): string {
    if (!v) return "";
    if (typeof v === "string") return v;
    const u = v.url || v.path || "";
    if (!u) return "";
    if (/^https?:\/\//i.test(u)) return u;
    return `${window.location.origin.replace(/\/+$/, "")}${u.startsWith("/") ? "" : "/"}${u}`;
}

/* ================== UI PRIMITIVES ================== */
function TextInput(props: {
    label: string;
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
}) {
    return (
        <div style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>{props.label}</div>
            <input
                value={props.value ?? ""}
                onChange={(e) => props.onChange(e.target.value)}
                placeholder={props.placeholder || ""}
                style={{
                    width: "100%",
                    padding: "8px 10px",
                    border: "1px solid #e2e8f0",
                    borderRadius: 8,
                }}
            />
        </div>
    );
}

function TextArea(props: {
    label: string;
    value: string;
    onChange: (v: string) => void;
    rows?: number;
    placeholder?: string;
}) {
    return (
        <div style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>{props.label}</div>
            <textarea
                rows={props.rows || 6}
                value={props.value ?? ""}
                onChange={(e) => props.onChange(e.target.value)}
                placeholder={props.placeholder || ""}
                style={{
                    width: "100%",
                    padding: "8px 10px",
                    border: "1px solid #e2e8f0",
                    borderRadius: 8,
                }}
            />
        </div>
    );
}

function Checkbox(props: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
    return (
        <label style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
            <input type="checkbox" checked={!!props.checked} onChange={(e) => props.onChange(e.target.checked)} />
            <span>{props.label}</span>
        </label>
    );
}

function Select(props: {
    label: string;
    value: string;
    onChange: (v: string) => void;
    options: Array<{ value: string; label: string }>;
}) {
    return (
        <div style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>{props.label}</div>
            <select
                value={props.value ?? ""}
                onChange={(e) => props.onChange(e.target.value)}
                style={{
                    width: "100%",
                    padding: "8px 10px",
                    border: "1px solid #e2e8f0",
                    borderRadius: 8,
                }}
            >
                <option value="">—</option>
                {props.options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                        {opt.label}
                    </option>
                ))}
            </select>
        </div>
    );
}

function ImageInput(props: { label: string; value: ImageValue; onChange: (v: ImageValue) => void; placeholder?: string }) {
    const [isDragging, setDragging] = React.useState(false);
    const [isUploading, setUploading] = React.useState(false);
    const [progress, setProgress] = React.useState(0);
    const [error, setError] = React.useState<string | null>(null);
    const fileRef = React.useRef<HTMLInputElement>(null);

    const displayUrl = resolvePreviewUrl(props.value);

    async function uploadFile(file: File) {
        setError(null);
        setUploading(true);
        setProgress(0);
        try {
            const fd = new FormData();
            fd.append("file", file);

            const { data } = await siteClient.post("/assets/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: (e) => {
                    if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
                },
            });

            if (Number(data?.status) !== 200) throw new Error(String(data?.data || "Upload failed"));

            props.onChange(data?.data || {});
        } catch (e: any) {
            setError(e?.message || "Ошибка загрузки");
        } finally {
            setUploading(false);
            setProgress(0);
        }
    }

    function onFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
        const f = e.target.files?.[0];
        if (f) uploadFile(f);
        e.currentTarget.value = "";
    }

    function onDrop(e: React.DragEvent<HTMLDivElement>) {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);
        const f = e.dataTransfer.files?.[0];
        if (f) uploadFile(f);
    }

    function onPaste(e: React.ClipboardEvent<HTMLDivElement>) {
        const item = Array.from(e.clipboardData.items).find((i) => i.type.startsWith("image/"));
        if (item) {
            const f = item.getAsFile();
            if (f) uploadFile(f);
        }
    }

    function onUrlChange(v: string) {
        props.onChange(v.trim());
    }

    const currentText = typeof props.value === "string" ? props.value : isObj(props.value) ? props.value.url || props.value.path || "" : "";

    return (
        <div style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>{props.label}</div>

            <input
                value={currentText}
                placeholder={props.placeholder || "Вставь URL или загрузи файл ниже"}
                onChange={(e) => onUrlChange(e.target.value)}
                style={{
                    width: "100%",
                    padding: "8px 10px",
                    border: "1px solid #e2e8f0",
                    borderRadius: 8,
                    marginBottom: 10,
                    fontFamily: "inherit",
                }}
            />

            <div
                onDragOver={(e) => {
                    e.preventDefault();
                    setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                onPaste={onPaste}
                style={{
                    border: `2px dashed ${isDragging ? "#4f46e5" : "#cbd5e1"}`,
                    borderRadius: 10,
                    background: "#f8fafc",
                    padding: 14,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 10,
                    cursor: "pointer",
                }}
                onClick={() => fileRef.current?.click()}
                title="Нажми, чтобы выбрать файл. Работает drag&drop и вставка из буфера."
            >
                <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={onFileInputChange} />
                <span style={{ fontWeight: 600 }}>Выбрать файл</span>
                <span style={{ color: "#64748b" }}>или перетащи изображение сюда • поддерживает paste</span>
                {isUploading && <span style={{ marginLeft: "auto" }}>{progress > 0 ? `Загрузка ${progress}%` : "Загрузка…"}</span>}
            </div>

            {error && <div style={{ color: "#b91c1c", marginBottom: 10 }}>{error}</div>}

            {displayUrl ? (
                <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, padding: 8, background: "#fff" }}>
                    <img src={displayUrl} alt="" style={{ maxWidth: "100%", height: "auto", display: "block" }} />
                    {isObj(props.value) && (
                        <div style={{ fontSize: 12, color: "#64748b", marginTop: 6, wordBreak: "break-all" }}>saved: {props.value.url || props.value.path}</div>
                    )}
                </div>
            ) : (
                <div style={{ fontSize: 12, color: "#64748b" }}>Превью появится после ввода URL или загрузки файла</div>
            )}
        </div>
    );
}

/* ================== UTILS ================== */
function randomKey(): string {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
}
function getByPath(obj: any, path: string) {
    if (!obj) return undefined;
    return path.replace(/\[(\w+)\]/g, ".$1").split(".").reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
}
function setByPath(obj: any, path: string, val: any) {
    const norm = path.replace(/\[(\w+)\]/g, ".$1").split(".");
    const last = norm.pop()!;
    let cur = obj || {};
    for (const k of norm) {
        if (cur[k] == null || typeof cur[k] !== "object") cur[k] = {};
        cur = cur[k];
    }
    cur[last] = val;
    return obj;
}
const toBool = (v: any) => {
    if (typeof v === "boolean") return v;
    if (typeof v === "number") return v !== 0;
    if (typeof v === "string") return ["1", "true", "yes", "on"].includes(v.trim().toLowerCase());
    return false;
};

/**
 * GenericSectionEditor — редактор по мета-схеме.
 */
const GenericSectionEditor: React.FC<Props> = ({ meta, value, onChange }) => {
    const model = React.useMemo(() => {
        if (typeof value === "string") {
            try {
                return JSON.parse(value) || {};
            } catch {
                return {}; // невалидный JSON — пусть будет пусто
            }
        }
        return value || {};
    }, [value]);

    const fields = meta.fields || [];

    const setField = (name: string, nextValue: any) => {
        const next = { ...model };
        // если используешь setByPath:
        // setByPath(next, name, nextValue);
        // иначе простая запись:
        (next as any)[name] = nextValue;

        // если наверху ждут объект:
        onChange(next);

        // если наверху ждут СТРОКУ — раскомментируй:
        // onChange(JSON.stringify(next));
    };

    const renderField = (field: MetaField) => {
        const fname = (field as any).name;
        const current = (model as any)?.[fname];

        switch (field.type) {
            case "string":
                return (
                    <TextInput
                        key={`${fname}:string`}
                        label={field.label}
                        value={current ?? (field as any).default ?? ""}
                        placeholder={(field as any).placeholder}
                        onChange={(v) => setField(fname, v)}
                    />
                );

            case "text":
                return (
                    <TextArea
                        key={`${field.name}:text`}
                        label={field.label}
                        value={current ?? (field as any).default ?? ""}
                        placeholder={(field as any).placeholder}
                        rows={6}
                        onChange={(v) => setField(field.name, v)}
                    />
                );

            case "html":
                return (
                    <TextArea
                        key={`${field.name}:html`}
                        label={field.label}
                        value={current ?? (field as any).default ?? ""}
                        rows={10}
                        onChange={(v) => setField(field.name, v)}
                    />
                );

            case "checkbox":
                return (
                    <Checkbox
                        key={`${field.name}:checkbox`}
                        label={field.label}
                        checked={toBool(current ?? (field as any).default ?? false)}
                        onChange={(v) => setField(field.name, v)}
                    />
                );

            case "select":
                return (
                    <Select
                        key={`${field.name}:select`}
                        label={field.label}
                        value={String(current ?? (field as any).default ?? "")}
                        options={(field as any).options || []}
                        onChange={(v) => setField(field.name, v)}
                    />
                );

            case "image":
                return (
                    <ImageInput
                        key={`${field.name}:image`}
                        label={field.label}
                        value={current ?? (field as any).default ?? ""}
                        placeholder={(field as any).placeholder}
                        onChange={(v) => setField(fname, v)}
                    />
                );

            case "repeater": {
                const normalizeItem = (row: any) => {
                    const r = { ...(row || {}) };
                    if (r.title == null) r.title = r.question ?? r.q ?? "";
                    if (r.text == null) r.text = r.answer ?? r.a ?? "";
                    return r;
                };

                let list: any[] = [];
                if (Array.isArray(current)) list = current.map(normalizeItem);
                else if (current && typeof current === "object") list = Object.values(current as Record<string, any>).map(normalizeItem);
                else if (typeof current === "string") {
                    try {
                        const parsed = JSON.parse(current);
                        list = Array.isArray(parsed) ? parsed.map(normalizeItem) : [];
                    } catch {
                        list = [];
                    }
                }

                if (list.length === 0) list = ((field as any).default || []).map(normalizeItem);

                const subgroupFields = (field as any).fields as MetaField[];
                const itemLabel = (field as any).itemLabel || field.label;

                const setList = (next: any[]) => setField(field.name, next);

                const addItem = () => {
                    const blank: any = { __key: randomKey(), __ui: { open: true } };
                    subgroupFields.forEach((sf) => {
                        const t = (sf as any).type;
                        const n = (sf as any).name;
                        if (t === "repeater") blank[n] = Array.isArray((sf as any).default) ? (sf as any).default : [];
                        else blank[n] = (sf as any).default ?? (t === "checkbox" ? false : "");
                    });
                    setList([...(list || []), blank]);
                };

                const removeItem = (i: number) => {
                    const next = list.filter((_, idx) => idx !== i);
                    setList(next);
                };

                const setItemField = (i: number, name: string, v: any) => {
                    const row = list[i] || {};
                    const ui = row.__ui ?? { open: true };
                    const rowWithKey = row.__key ? row : { __key: randomKey(), ...row };
                    const next = [...list];
                    next[i] = { ...rowWithKey, [name]: v, __ui: ui };
                    setList(next);
                };

                const toggleOpen = (i: number, val: boolean) => {
                    const row = list[i] || {};
                    const next = [...list];
                    next[i] = { ...(row.__key ? row : { __key: randomKey(), ...row }), __ui: { ...(row.__ui || {}), open: val } };
                    setList(next);
                };

                /* -------- вложенные поля -------- */
                const renderSubField = (sf: MetaField, i: number) => {
                    const row = list[i] || {};
                    const val = row?.[(sf as any).name];

                    switch ((sf as any).type) {
                        case "string":
                            return (
                                <TextInput
                                    key={`${(sf as any).name}:string:${i}`}
                                    label={(sf as any).label}
                                    value={val ?? (sf as any).default ?? ""}
                                    onChange={(v) => setItemField(i, (sf as any).name, v)}
                                />
                            );
                        case "text":
                            return (
                                <TextArea
                                    key={`${(sf as any).name}:text:${i}`}
                                    label={(sf as any).label}
                                    value={val ?? (sf as any).default ?? ""}
                                    rows={6}
                                    onChange={(v) => setItemField(i, (sf as any).name, v)}
                                />
                            );
                        case "html":
                            return (
                                <TextArea
                                    key={`${(sf as any).name}:html:${i}`}
                                    label={(sf as any).label}
                                    value={val ?? (sf as any).default ?? ""}
                                    rows={10}
                                    onChange={(v) => setItemField(i, (sf as any).name, v)}
                                />
                            );
                        case "checkbox":
                            return (
                                <Checkbox
                                    key={`${(sf as any).name}:checkbox:${i}`}
                                    label={(sf as any).label}
                                    checked={toBool(val ?? (sf as any).default ?? false)}
                                    onChange={(v) => setItemField(i, (sf as any).name, v)}
                                />
                            );
                        case "select":
                            return (
                                <Select
                                    key={`${(sf as any).name}:select:${i}`}
                                    label={(sf as any).label}
                                    value={String(val ?? (sf as any).default ?? "")}
                                    options={(sf as any).options || []}
                                    onChange={(v) => setItemField(i, (sf as any).name, v)}
                                />
                            );
                        case "image":
                            return (
                                <ImageInput
                                    key={`${(sf as any).name}:image:${i}`}
                                    label={(sf as any).label}
                                    value={val ?? (sf as any).default ?? ""}
                                    onChange={(v) => setItemField(i, (sf as any).name, v)}
                                />
                            );

                        case "repeater": {
                            const normalizeSubItem = (row2: any) => {
                                const r = { ...(row2 || {}) };
                                if (r.title == null) r.title = r.question ?? r.q ?? "";
                                if (r.text == null) r.text = r.answer ?? r.a ?? "";
                                return r;
                            };

                            let subList: any[] = [];
                            if (Array.isArray(val)) subList = val.map(normalizeSubItem);
                            else if (val && typeof val === "object") subList = Object.values(val as Record<string, any>).map(normalizeSubItem);
                            else if (typeof val === "string") {
                                try {
                                    const parsed = JSON.parse(val);
                                    if (Array.isArray(parsed)) subList = parsed.map(normalizeSubItem);
                                } catch {}
                            }
                            if (subList.length === 0) subList = ((sf as any).default || []).map(normalizeSubItem);

                            const subFields = (sf as any).fields as MetaField[];
                            const subItemLabel = (sf as any).itemLabel || (sf as any).label;

                            const setSubList = (next: any[]) => setItemField(i, (sf as any).name, next);

                            const addSubItem = () => {
                                const blank: any = { __key: randomKey(), __ui: { open: true } };
                                subFields.forEach((ssf) => {
                                    const t = (ssf as any).type;
                                    const n = (ssf as any).name;
                                    if (t === "repeater") blank[n] = Array.isArray((ssf as any).default) ? (ssf as any).default : [];
                                    else blank[n] = (ssf as any).default ?? (t === "checkbox" ? false : "");
                                });
                                setSubList([...(subList || []), blank]);
                            };

                            const removeSubItem = (k: number) => {
                                const next = subList.filter((_: any, idx: number) => idx !== k);
                                setSubList(next);
                            };

                            const setSubItemField = (k: number, name: string, v: any) => {
                                const row2 = subList[k] || {};
                                const ui2 = row2.__ui ?? { open: true };
                                const row2WithKey = row2.__key ? row2 : { __key: randomKey(), ...row2 };
                                const next = [...subList];
                                next[k] = { ...row2WithKey, [name]: v, __ui: ui2 };
                                setSubList(next);
                            };

                            const toggleSubOpen = (k: number, val: boolean) => {
                                const row2 = subList[k] || {};
                                const next = [...subList];
                                next[k] = { ...(row2.__key ? row2 : { __key: randomKey(), ...row2 }), __ui: { ...(row2.__ui || {}), open: val } };
                                setSubList(next);
                            };

                            const renderSubSubField = (ssf: MetaField, k: number) => {
                                const row2 = subList[k] || {};
                                const val2 = row2?.[(ssf as any).name];

                                switch ((ssf as any).type) {
                                    case "string":
                                        return (
                                            <TextInput
                                                key={`${(ssf as any).name}:string:${i}:${k}`}
                                                label={(ssf as any).label}
                                                value={val2 ?? (ssf as any).default ?? ""}
                                                onChange={(v) => setSubItemField(k, (ssf as any).name, v)}
                                            />
                                        );
                                    case "text":
                                        return (
                                            <TextArea
                                                key={`${(ssf as any).name}:text:${i}:${k}`}
                                                label={(ssf as any).label}
                                                value={val2 ?? (ssf as any).default ?? ""}
                                                rows={6}
                                                onChange={(v) => setSubItemField(k, (ssf as any).name, v)}
                                            />
                                        );
                                    case "html":
                                        return (
                                            <TextArea
                                                key={`${(ssf as any).name}:html:${i}:${k}`}
                                                label={(ssf as any).label}
                                                value={val2 ?? (ssf as any).default ?? ""}
                                                rows={10}
                                                onChange={(v) => setSubItemField(k, (ssf as any).name, v)}
                                            />
                                        );
                                    case "checkbox":
                                        return (
                                            <Checkbox
                                                key={`${(ssf as any).name}:checkbox:${i}:${k}`}
                                                label={(ssf as any).label}
                                                checked={toBool(val2 ?? (ssf as any).default ?? false)}
                                                onChange={(v) => setSubItemField(k, (ssf as any).name, v)}
                                            />
                                        );
                                    case "select":
                                        return (
                                            <Select
                                                key={`${(ssf as any).name}:select:${i}:${k}`}
                                                label={(ssf as any).label}
                                                value={String(val2 ?? (ssf as any).default ?? "")}
                                                options={(ssf as any).options || []}
                                                onChange={(v) => setSubItemField(k, (ssf as any).name, v)}
                                            />
                                        );
                                    case "image":
                                        return (
                                            <ImageInput
                                                key={`${(ssf as any).name}:image:${i}:${k}`}
                                                label={(ssf as any).label}
                                                value={val2 ?? (ssf as any).default ?? ""}
                                                onChange={(v) => setSubItemField(k, (ssf as any).name, v)}
                                            />
                                        );
                                    default:
                                        return null;
                                }
                            };

                            return (
                                <div
                                    key={`${(sf as any).name}:nested-repeater:${i}`}
                                    style={{ marginBottom: 12, border: "1px dashed #e5e7eb", borderRadius: 8, padding: 10 }}
                                >
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                                        <div style={{ fontWeight: 600 }}>{(sf as any).label}</div>
                                        <button type="button" className="btn btn-sm btn-light" onClick={addSubItem}>
                                            Добавить
                                        </button>
                                    </div>

                                    {subList.length === 0 && (
                                        <div className="text-muted" style={{ fontSize: 13 }}>
                                            Список пуст
                                        </div>
                                    )}

                                    {subList.map((row2, k) => {
                                        const row2Key = row2?.__key || `${(sf as any).name}:tmp:${i}:${k}`;
                                        const open2 = row2?.__ui?.open !== false;
                                        return (
                                            <div key={row2Key} style={{ background: "#fff", border: "1px solid #f1f5f9", borderRadius: 8, padding: 10, marginBottom: 10 }}>
                                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                                                    <div style={{ fontWeight: 600, fontSize: 13 }}>{`${subItemLabel} #${k + 1}`}</div>
                                                    <div className="d-flex gap-2">
                                                        <button type="button" className="btn btn-sm btn-light" onClick={() => toggleSubOpen(k, !open2)}>
                                                            {open2 ? "Свернуть" : "Развернуть"}
                                                        </button>
                                                        <button type="button" className="btn btn-sm btn-danger" onClick={() => removeSubItem(k)}>
                                                            Удалить
                                                        </button>
                                                    </div>
                                                </div>
                                                <div style={{ display: open2 ? "block" : "none" }}>
                                                    {subFields.map((ssf, j2) => (
                                                        <div key={`${row2Key}:${(ssf as any).name}:${j2}`} style={{ marginBottom: 10 }}>
                                                            {renderSubSubField(ssf, k)}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        }

                        default:
                            return null;
                    }
                };

                return (
                    <div key={`${field.name}:repeater`} style={{ marginBottom: 16, border: "1px dashed #e2e8f0", borderRadius: 10, padding: 12 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                            <div style={{ fontWeight: 700 }}>{field.label}</div>
                            <button type="button" className="btn btn-sm btn-light" onClick={addItem}>
                                Добавить
                            </button>
                        </div>

                        {list.length === 0 && (
                            <div className="text-muted" style={{ fontSize: 13 }}>
                                Список пуст
                            </div>
                        )}

                        {list.map((row, i) => {
                            const rowKey = row?.__key || `${field.name}:tmp:${i}`;
                            const open = row?.__ui?.open !== false;

                            return (
                                <div key={rowKey} style={{ background: "#fff", border: "1px solid #f1f5f9", borderRadius: 8, padding: 10, marginBottom: 10 }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                                        <div style={{ fontWeight: 600, fontSize: 13 }}>{itemLabel} #{i + 1}</div>
                                        <div className="d-flex gap-2">
                                            <button type="button" className="btn btn-sm btn-light" onClick={() => toggleOpen(i, !open)}>
                                                {open ? "Свернуть" : "Развернуть"}
                                            </button>
                                            <button type="button" className="btn btn-sm btn-danger" onClick={() => removeItem(i)}>
                                                Удалить
                                            </button>
                                        </div>
                                    </div>

                                    <div style={{ display: open ? "block" : "none" }}>
                                        {subgroupFields.map((sf, j) => (
                                            <div key={`${rowKey}:${(sf as any).name}:${j}`} style={{ marginBottom: 10 }}>
                                                {renderSubField(sf, i)}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                );
            }

            default:
                return null;
        }
    };

    return (
        <div style={{ background: "#fff", padding: 16, border: "1px dashed #cbd5e1", borderRadius: 10 }}>
            {meta.notes && (
                <div style={{ marginBottom: 12, color: "#64748b", fontSize: 13 }}>{meta.notes}</div>
            )}
            {(fields || []).length === 0 && <div className="text-muted">Нет полей для редактирования</div>}
            {fields.map((f) => renderField(f))}
        </div>
    );
};

export default GenericSectionEditor;
